// Runtime typography CSS: unlayered so it beats @layer nxt.tokens / nx.tokens.

export const RUNTIME_TYPE_STYLE_ID = 'nx-runtime-type';

export const UI_FONT_STACKS = {
  basier: "'Basier Circle', system-ui, sans-serif",
  system: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Iowan Old Style', 'Palatino Linotype', serif",
  mono: "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace",
} as const;

export const MONO_FONT_STACKS = {
  system: "ui-monospace, 'SFMono-Regular', Menlo, Consolas, monospace",
} as const;

export type NamedUiFont = keyof typeof UI_FONT_STACKS;
export type NamedMonoFont = 'system' | 'ui';
export type ShellUiFont = NamedUiFont | 'custom';

export const SUPPORTED_UI_FONTS: readonly ShellUiFont[] = [
  'basier',
  'system',
  'serif',
  'mono',
  'custom',
] as const;

export const SUPPORTED_MONO_FONTS: readonly NamedMonoFont[] = ['system', 'ui'] as const;

export const FONT_SIZE_STEPS = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'] as const;

export const RUNTIME_TYPE_SCALE_MIN = 0.5;
export const RUNTIME_TYPE_SCALE_MAX = 1.4;
export const RUNTIME_TYPE_SCALE_STEP = 0.025;
export const RUNTIME_TYPE_SCALE_PRESETS = [0.75, 0.875, 1, 1.125, 1.25] as const;

/** Icon scale (I-2820): multiplies every `--nxt-icon-size-*` on top of the font
 *  and area scales, so a look sets its icons bigger or smaller than its text. */
export const RUNTIME_ICON_SCALE_MIN = 0.75;
export const RUNTIME_ICON_SCALE_MAX = 1.5;
export const RUNTIME_ICON_SCALE_STEP = 0.025;
export const RUNTIME_ICON_SCALE_PRESETS = [0.875, 1, 1.125, 1.25] as const;

/** Areas that can carry their own scale on top of the root font scale
 *  (`data-nx-scope="<area>"` on the shell, `--nxt-scale-<area>` in CSS). */
export const RUNTIME_TYPE_SCOPES = ['navbar', 'sidebar', 'content', 'dock', 'table'] as const;
export type RuntimeTypeScope = (typeof RUNTIME_TYPE_SCOPES)[number];
/** Sparse: only areas that differ from 1 are stored. */
export type RuntimeTypeScopes = Partial<Record<RuntimeTypeScope, number>>;
export const RUNTIME_TYPE_SCOPE_SCALE_MIN = 0.7;
export const RUNTIME_TYPE_SCOPE_SCALE_MAX = 1.3;

export type RuntimeTypeConfig = {
  font: ShellUiFont;
  mono: NamedMonoFont;
  scale: number;
  customFont?: string;
  scopes?: RuntimeTypeScopes;
  /** Sparse: absent means 1 (icons follow the text). */
  iconScale?: number;
};

export const DEFAULT_RUNTIME_TYPE: RuntimeTypeConfig = {
  font: 'basier',
  mono: 'system',
  scale: 1,
};

const FONT_FAMILY_OK = /^[-a-zA-Z0-9\s'",.]+$/;

/** Reject CSS/HTML injection; allow a normal font-family list. */
export function parseFontFamily(value: string): string | null {
  const v = value.trim().replace(/\s+/g, ' ');
  if (!v || v.length > 160) return null;
  if (/[{};@<>\\]|url\s*\(|expression/i.test(v)) return null;
  if (!FONT_FAMILY_OK.test(v)) return null;
  return v;
}

export function parseTypeScale(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 1000) / 1000;
  if (rounded < RUNTIME_TYPE_SCALE_MIN || rounded > RUNTIME_TYPE_SCALE_MAX) return null;
  return rounded;
}

/** The look's icon scale, rounded; `null` when missing or out of range. */
export function parseIconScale(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 1000) / 1000;
  if (rounded < RUNTIME_ICON_SCALE_MIN || rounded > RUNTIME_ICON_SCALE_MAX) return null;
  return rounded;
}

export function parseScopeScale(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN;
  if (!Number.isFinite(n)) return null;
  const rounded = Math.round(n * 1000) / 1000;
  if (rounded < RUNTIME_TYPE_SCOPE_SCALE_MIN || rounded > RUNTIME_TYPE_SCOPE_SCALE_MAX) return null;
  return rounded;
}

/** Lenient: unknown areas and invalid values are dropped, 1 is not stored. */
export function parseRuntimeTypeScopes(value: unknown): RuntimeTypeScopes | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const rec = value as Record<string, unknown>;
  const out: RuntimeTypeScopes = {};
  for (const scope of RUNTIME_TYPE_SCOPES) {
    const parsed = parseScopeScale(rec[scope]);
    if (parsed != null && parsed !== 1) out[scope] = parsed;
  }
  return Object.keys(out).length ? out : undefined;
}

export function parseRuntimeTypeConfig(value: unknown): RuntimeTypeConfig | null {
  if (!value || typeof value !== 'object') return null;
  const rec = value as Record<string, unknown>;
  const font = rec.font;
  if (typeof font !== 'string' || !(SUPPORTED_UI_FONTS as readonly string[]).includes(font)) {
    return null;
  }
  const mono = rec.mono;
  if (typeof mono !== 'string' || !(SUPPORTED_MONO_FONTS as readonly string[]).includes(mono)) {
    return null;
  }
  const scale = parseTypeScale(rec.scale);
  if (scale == null) return null;
  const out: RuntimeTypeConfig = {
    font: font as ShellUiFont,
    mono: mono as NamedMonoFont,
    scale,
  };
  if (font === 'custom') {
    const custom =
      typeof rec.customFont === 'string' ? parseFontFamily(rec.customFont) : null;
    if (!custom) return null;
    out.customFont = custom;
  }
  const scopes = parseRuntimeTypeScopes(rec.scopes);
  if (scopes) out.scopes = scopes;
  // Lenient like the scopes: an invalid icon scale is dropped, 1 is not stored.
  const iconScale = parseIconScale(rec.iconScale);
  if (iconScale != null && iconScale !== 1) out.iconScale = iconScale;
  return out;
}

export function isDefaultRuntimeType(config: RuntimeTypeConfig): boolean {
  return (
    config.font === 'basier' &&
    config.mono === 'system' &&
    config.scale === 1 &&
    !config.scopes &&
    !config.iconScale
  );
}

export function resolveUiFontStack(config: RuntimeTypeConfig): string {
  if (config.font === 'custom') {
    return config.customFont ?? UI_FONT_STACKS.basier;
  }
  return UI_FONT_STACKS[config.font];
}

export function resolveMonoFontStack(config: RuntimeTypeConfig): string {
  if (config.mono === 'ui') return resolveUiFontStack(config);
  return MONO_FONT_STACKS.system;
}

/**
 * Typography overrides. Unlayered on purpose: they must beat `@layer nxt.tokens`
 * and the legacy `--nx-font*` contract so leftover module CSS follows the scale.
 */
export function emitRuntimeTypeCss(config: RuntimeTypeConfig): string {
  const parsed = parseRuntimeTypeConfig(config);
  if (!parsed) {
    throw new Error('nx-tokens: runtime type config is invalid');
  }
  const ui = resolveUiFontStack(parsed);
  const mono = resolveMonoFontStack(parsed);
  const nxSizes = FONT_SIZE_STEPS.map(
    (step) => `  --nx-font-size-${step}: var(--nxt-font-size-${step});`
  ).join('\n');
  // Per-area scales: every area is written (1 for the rest) so switching
  // layouts never leaves a stale factor behind.
  const scopeLines = RUNTIME_TYPE_SCOPES.map(
    (scope) => `  --nxt-scale-${scope}: ${parsed.scopes?.[scope] ?? 1};`
  ).join('\n');
  return (
    `/* runtime type — unlayered so it beats the nxt.tokens / nx.tokens layers */\n` +
    `:root {\n` +
    `  --nxt-font: ${ui};\n` +
    `  --nxt-font-mono: ${mono};\n` +
    `  --nxt-font-scale: ${parsed.scale};\n` +
    `  --nxt-icon-scale: ${parsed.iconScale ?? 1};\n` +
    `${scopeLines}\n` +
    `  --nx-font: var(--nxt-font);\n` +
    `  --nx-font-mono: var(--nxt-font-mono);\n` +
    `${nxSizes}\n` +
    `}\n` +
    `html, body {\n` +
    `  font-family: var(--nxt-font);\n` +
    `}\n`
  );
}
